import axios from 'axios';
import { getToken, getCurrentOrgId } from './authClient';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance with auth interceptor
const api = axios.create({
    baseURL: API_URL,
});

// Add auth token and org_id to all requests
api.interceptors.request.use(async (config) => {
    const token = await getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Add org_id header for multi-tenant isolation
    const orgId = getCurrentOrgId();
    if (orgId) {
        config.headers['X-Org-ID'] = orgId;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Add response interceptor to handle stale org_id 403s
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 403) {
            // Check if it's the specific organization access error
            if (error.response.data && error.response.data.detail === "User does not have access to this organization") {
                console.warn("Stale organization ID detected. Clearing localStorage and reloading...");
                localStorage.removeItem('driftops_current_org_id');
                window.location.reload();
            }
        }
        return Promise.reject(error);
    }
);

// Export for direct use if needed
export { api };

// Default export for convenience
export default api;

// --- Retry Utility ---

/**
 * Wraps an async function with retry logic and exponential backoff.
 * @param {Function} fn - The async function to wrap
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum retry attempts (default: 3)
 * @param {number} options.baseDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {boolean} options.silent - If true, don't log retry attempts
 * @returns {Promise} - Result of the function call
 */
export const withRetry = async (fn, options = {}) => {
    const {
        maxRetries = 3,
        baseDelay = 1000,
        maxDelay = 10000,
        silent = false
    } = options;

    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;

            // Don't retry on 4xx errors (client errors)
            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                throw error;
            }

            if (attempt < maxRetries) {
                const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
                if (!silent) {
                    console.warn(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}). Retrying in ${delay}ms...`);
                }
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    throw lastError;
};

// --- Health Check ---

/**
 * Check if the backend is healthy and database is connected.
 * @returns {Promise<{status: string, database: string, ready: boolean}>}
 */
export const checkHealth = async () => {
    const response = await api.get(`${API_URL}/api/health`);
    return response.data;
};

/**
 * Wait for the backend to be ready with retry logic.
 * Useful on app initialization.
 * @param {number} maxAttempts - Maximum attempts (default: 5)
 * @param {number} delay - Delay between attempts in ms (default: 2000)
 * @returns {Promise<boolean>} - True if backend is ready
 */
export const waitForBackend = async (maxAttempts = 5, delay = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const health = await checkHealth();
            if (health.ready) {
                return true;
            }
        } catch (error) {
            console.warn(`Backend health check failed (attempt ${attempt + 1}/${maxAttempts})`);
        }

        if (attempt < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    return false;
};

// --- Comparator ---

export const compareFiles = async (sourceFile, targetFile, settings = {}) => {
    const formData = new FormData();
    formData.append('source_file', sourceFile);
    formData.append('target_file', targetFile);

    // Add optional matching settings as query params
    const params = new URLSearchParams();
    if (settings.contextWeight !== undefined) params.append('context_weight', settings.contextWeight);
    if (settings.confidenceThreshold !== undefined) params.append('threshold', settings.confidenceThreshold);

    const queryString = params.toString();
    const url = `${API_URL}/api/compare/files${queryString ? '?' + queryString : ''}`;

    const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const compareText = async (sourceText, targetText, settings = {}) => {
    const response = await api.post(`${API_URL}/api/compare/text`, {
        source_text: sourceText,
        target_text: targetText,
        context_weight: settings.contextWeight,
        threshold: settings.confidenceThreshold,
    });
    return response.data;
};

export const compareContract = async (contractId, targetFile, settings = {}) => {
    const formData = new FormData();
    formData.append('target_file', targetFile);

    // Add optional matching settings as query params
    const params = new URLSearchParams();
    if (settings.contextWeight !== undefined) params.append('context_weight', settings.contextWeight);
    if (settings.confidenceThreshold !== undefined) params.append('threshold', settings.confidenceThreshold);

    const queryString = params.toString();
    const url = `${API_URL}/api/compare/contract/${contractId}${queryString ? '?' + queryString : ''}`;

    const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

// --- Billing (Stripe) ---

export const createStripeCheckout = async (priceId) => {
    const response = await api.post(`${API_URL}/api/stripe/checkout`, { price_id: priceId });
    return response.data;
};

export const createStripePortal = async () => {
    const response = await api.post(`${API_URL}/api/stripe/portal`);
    return response.data;
};

export const getSubscriptionInfo = async () => {
    const response = await api.get(`${API_URL}/api/stripe/subscription`);
    return response.data;
};

export const parseFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${API_URL}/api/compare/parse/file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};


// --- Contracts ---

export const getContracts = async (options = {}) => {
    return withRetry(async () => {
        const params = new URLSearchParams();
        if (options.search) params.append('search', options.search);
        if (options.tag) params.append('tag', options.tag);
        if (options.skip) params.append('skip', options.skip);
        if (options.limit) params.append('limit', options.limit);
        if (options.vendor) params.append('vendor', options.vendor);
        if (options.groupBy) params.append('group_by', options.groupBy);

        const queryString = params.toString();
        const url = `${API_URL}/api/contracts/${queryString ? '?' + queryString : ''}`;
        const response = await api.get(url);
        // Backend returns paginated {items, next_cursor, has_more} — extract the array
        const data = response.data;
        return Array.isArray(data) ? data : (data.items || []);
    }, { maxRetries: 3, baseDelay: 1000 });
};

export const getContract = async (contractId) => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}`);
    return response.data;
};

export const createContract = async (contract) => {
    const response = await api.post(`${API_URL}/api/contracts/`, contract);
    return response.data;
};

export const updateContract = async (contractId, updates) => {
    const response = await api.patch(`${API_URL}/api/contracts/${contractId}`, updates);
    return response.data;
};

export const deleteContract = async (contractId) => {
    const response = await api.delete(`${API_URL}/api/contracts/${contractId}`);
    return response.data;
};

export const exportContract = async (contractId, format = 'full') => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}/export`, {
        params: { format }
    });
    return response.data;
};

export const getContractStats = async (contractId) => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}/stats`);
    return response.data;
};

export const getContractExports = async (contractId) => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}/exports`);
    return response.data;
};

export const updateContractExports = async (contractId, destinations) => {
    const response = await api.put(`${API_URL}/api/contracts/${contractId}/exports`, {
        destinations
    });
    return response.data;
};

// --- Partner Notification ---

export const getPartnerNotificationConfig = async (contractId) => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}/partner-notification`);
    return response.data;
};

export const updatePartnerNotificationConfig = async (contractId, config) => {
    const response = await api.put(`${API_URL}/api/contracts/${contractId}/partner-notification`, config);
    return response.data;
};

export const testPartnerNotification = async (contractId) => {
    const response = await api.post(`${API_URL}/api/contracts/${contractId}/partner-notification/test`);
    return response.data;
};

// --- OpenAPI Export ---

export const exportContractOpenAPI = async (contractId, format = 'yaml') => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}/export/openapi`, {
        params: { format },
        responseType: 'blob',
    });

    const ext = format === 'json' ? 'json' : 'yaml';
    const mimeType = format === 'json' ? 'application/json' : 'application/x-yaml';
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contract_${contractId}_openapi.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 150);
};

// Helper to trigger download of exported contract
export const downloadContract = async (contractId, format = 'full') => {
    const data = await exportContract(contractId, format);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = data.contractName
        ? `${data.contractName.replace(/\s+/g, '_')}_contract.json`
        : `contract_${contractId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return data;
};

// --- Drift Events ---

/**
 * Get drift events with cursor-based pagination (optimized for performance).
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status ('pending', 'resolved', 'ignored')
 * @param {string} options.cursor - Pagination cursor from previous response
 * @param {number} options.limit - Number of items per page (default 20)
 * @returns {Promise<{items: Array, next_cursor: string|null, has_more: boolean}>}
 */
export const getDriftEvents = async (options = {}) => {
    return withRetry(async () => {
        const params = new URLSearchParams();
        if (options.status) params.append('status', options.status);
        if (options.cursor) params.append('cursor', options.cursor);
        if (options.limit) params.append('limit', options.limit);

        const queryString = params.toString();
        const url = `${API_URL}/api/drift-events/${queryString ? '?' + queryString : ''}`;
        const response = await api.get(url);
        return response.data;
    }, { maxRetries: 3, baseDelay: 1000 });
};

/**
 * Get a single drift event with full details.
 * @param {number} eventId - The drift event ID
 */
export const getDriftEvent = async (eventId) => {
    const response = await api.get(`${API_URL}/api/drift-events/${eventId}`);
    return response.data;
};

/**
 * Get drift event content (source, target, or diff) separately.
 * Optimized for lazy loading large content after initial list view.
 * @param {number} eventId - The drift event ID
 * @param {string} contentType - Which content to return ('all', 'source', 'target', 'diff')
 */
export const getDriftEventContent = async (eventId, contentType = 'all') => {
    return withRetry(async () => {
        const response = await api.get(
            `${API_URL}/api/drift-events/${eventId}/content`,
            { params: { content_type: contentType } }
        );
        return response.data;
    }, { maxRetries: 2, baseDelay: 500 });
};

export const resolveDriftEvent = async (eventId, data = {}) => {
    const response = await api.patch(`${API_URL}/api/drift-events/${eventId}/resolve`, data);
    return response.data;
};

export const exportDriftEvent = async (eventId, type = 'target') => {
    const response = await api.get(`${API_URL}/api/drift-events/${eventId}/export`, {
        params: { type },
        responseType: 'blob',
    });
    // Extract filename from Content-Disposition header
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : `drift_event_${eventId}_export`;

    // Trigger browser download
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Delay revocation to ensure the browser has time to start the download
    setTimeout(() => {
        window.URL.revokeObjectURL(url);
    }, 150);

    return { success: true, filename };
};

export const ignoreDriftEvent = async (eventId) => {
    const response = await api.patch(`${API_URL}/api/drift-events/${eventId}/ignore`);
    return response.data;
};

export const rejectDriftEvent = async (eventId, data = {}) => {
    const response = await api.patch(`${API_URL}/api/drift-events/${eventId}/reject`, data);
    return response.data;
};

export const reopenDriftEvent = async (eventId) => {
    const response = await api.patch(`${API_URL}/api/drift-events/${eventId}/reopen`);
    return response.data;
};

export const deleteDriftEvent = async (eventId) => {
    const response = await api.delete(`${API_URL}/api/drift-events/${eventId}`);
    return response.data;
};

export const deleteDriftEvents = async (eventIds) => {
    const response = await api.post(`${API_URL}/api/drift-events/batch-delete`, { ids: eventIds });
    return response.data;
};

export const getDriftStats = async () => {
    const response = await api.get(`${API_URL}/api/drift-events/stats/summary`);
    return response.data;
};

export const getExportAttempts = async (eventId) => {
    const response = await api.get(`${API_URL}/api/drift-events/${eventId}/export-attempts`);
    return response.data;
};

export const testExport = async (eventId, destination) => {
    const response = await api.post(`${API_URL}/api/drift-events/${eventId}/test-export`, {
        destination
    });
    return response.data;
};

// --- Monitors ---

export const getMonitors = async (options = {}) => {
    const params = new URLSearchParams();
    if (options.is_active !== undefined) params.append('is_active', options.is_active);

    const queryString = params.toString();
    const url = `${API_URL}/api/monitors/${queryString ? '?' + queryString : ''}`;
    const response = await api.get(url);
    // Backend returns paginated {items, next_cursor, has_more} — extract the array
    const data = response.data;
    return Array.isArray(data) ? data : (data.items || []);
};

export const getMonitor = async (monitorId) => {
    const response = await api.get(`${API_URL}/api/monitors/${monitorId}`);
    return response.data;
};

export const createMonitor = async (data) => {
    const response = await api.post(`${API_URL}/api/monitors/`, data);
    return response.data;
};

export const updateMonitor = async (monitorId, data) => {
    const response = await api.patch(`${API_URL}/api/monitors/${monitorId}`, data);
    return response.data;
};

export const deleteMonitor = async (monitorId) => {
    const response = await api.delete(`${API_URL}/api/monitors/${monitorId}`);
    return response.data;
};

export const toggleMonitor = async (monitorId) => {
    const response = await api.post(`${API_URL}/api/monitors/${monitorId}/toggle`);
    return response.data;
};

export const regenerateMonitorToken = async (monitorId) => {
    const response = await api.post(`${API_URL}/api/monitors/${monitorId}/regenerate-token`);
    return response.data;
};

export const getWebhookStatus = async (token) => {
    const response = await api.get(`${API_URL}/api/webhook/${token}/status`);
    return response.data;
};

// --- Cloud Connections ---

export const getCloudConnections = async (options = {}) => {
    const params = new URLSearchParams();
    if (options.provider) params.append('provider', options.provider);
    if (options.is_active !== undefined) params.append('is_active', options.is_active);

    const queryString = params.toString();
    const url = `${API_URL}/api/cloud-connections/${queryString ? '?' + queryString : ''}`;
    const response = await api.get(url);
    return response.data;
};

export const getCloudConnection = async (connectionId) => {
    const response = await api.get(`${API_URL}/api/cloud-connections/${connectionId}`);
    return response.data;
};

export const createCloudConnection = async (data) => {
    const response = await api.post(`${API_URL}/api/cloud-connections/`, data);
    return response.data;
};

export const updateCloudConnection = async (connectionId, data) => {
    const response = await api.patch(`${API_URL}/api/cloud-connections/${connectionId}`, data);
    return response.data;
};

export const deleteCloudConnection = async (connectionId) => {
    const response = await api.delete(`${API_URL}/api/cloud-connections/${connectionId}`);
    return response.data;
};

export const toggleCloudConnection = async (connectionId) => {
    const response = await api.post(`${API_URL}/api/cloud-connections/${connectionId}/toggle`);
    return response.data;
};

export const testCloudConnection = async (connectionId) => {
    const response = await api.post(`${API_URL}/api/cloud-connections/${connectionId}/test`);
    return response.data;
};

export const pollCloudConnection = async (connectionId) => {
    const response = await api.post(`${API_URL}/api/cloud-connections/${connectionId}/poll-now`);
    return response.data;
};

export const updateCloudConnectionCredentials = async (connectionId, credentials) => {
    const response = await api.patch(`${API_URL}/api/cloud-connections/${connectionId}/credentials`, credentials);
    return response.data;
};

export const getWebhookUrl = async (connectionId) => {
    const response = await api.get(`${API_URL}/api/webhooks/url/${connectionId}`);
    return response.data;
};

export const getCloudHealthSummary = async () => {
    const response = await api.get(`${API_URL}/api/cloud-connections/health/summary`);
    return response.data;
};

export const getConnectionHealth = async (connectionId) => {
    const response = await api.get(`${API_URL}/api/cloud-connections/${connectionId}/health`);
    return response.data;
};

export const rotateConnectionCredentials = async (connectionId, credentials, expiresAt = null) => {
    const response = await api.post(`${API_URL}/api/cloud-connections/${connectionId}/rotate-credentials`, {
        credentials,
        expires_at: expiresAt
    });
    return response.data;
};

export const updateCredentialExpiration = async (connectionId, expiresAt, reminderDays = null) => {
    const params = new URLSearchParams();
    if (expiresAt) params.append('expires_at', expiresAt);
    if (reminderDays) params.append('reminder_days', reminderDays);
    const response = await api.patch(`${API_URL}/api/cloud-connections/${connectionId}/expiration?${params}`);
    return response.data;
};

export const clearConnectionCache = async (connectionId) => {
    const response = await api.post(`${API_URL}/api/cloud-connections/${connectionId}/clear-cache`);
    return response.data;
};

// --- Pipelines ---

export const getPipelines = async () => {
    const response = await api.get(`${API_URL}/api/pipelines/`);
    return response.data;
};

export const createPipeline = async (data) => {
    const response = await api.post(`${API_URL}/api/pipelines/`, data);
    return response.data;
};

export const getPipeline = async (pipelineId) => {
    const response = await api.get(`${API_URL}/api/pipelines/${pipelineId}`);
    return response.data;
};

export const updatePipeline = async (pipelineId, data) => {
    const response = await api.patch(`${API_URL}/api/pipelines/${pipelineId}`, data);
    return response.data;
};

export const deletePipeline = async (pipelineId) => {
    const response = await api.delete(`${API_URL}/api/pipelines/${pipelineId}`);
    return response.data;
};

export const togglePipeline = async (pipelineId) => {
    const response = await api.post(`${API_URL}/api/pipelines/${pipelineId}/toggle`);
    return response.data;
};

export const testPipeline = async (pipelineId) => {
    const response = await api.post(`${API_URL}/api/pipelines/${pipelineId}/test`);
    return response.data;
};

// --- Pipeline Activity ---

export const getPipelineEvents = async (contractId, limit = 10) => {
    const response = await api.get(`${API_URL}/api/drift-events/`, {
        params: { contract_id: contractId, limit },
    });
    return response.data;
};

// --- Contract Versions ---

export const getVersions = async (contractId) => {
    return withRetry(async () => {
        const response = await api.get(`${API_URL}/api/contracts/${contractId}/versions`);
        return response.data;
    }, { maxRetries: 3, baseDelay: 1000 });
};

export const getVersion = async (contractId, versionNumber) => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}/versions/${versionNumber}`);
    return response.data;
};

export const createVersion = async (contractId, data) => {
    const response = await api.post(`${API_URL}/api/contracts/${contractId}/versions`, data);
    return response.data;
};

export const saveAndResolve = async (contractId, data) => {
    const response = await api.post(`${API_URL}/api/contracts/${contractId}/save-and-resolve`, data);
    return response.data;
};

export const revertToVersion = async (contractId, versionNumber) => {
    const response = await api.post(`${API_URL}/api/contracts/${contractId}/revert/${versionNumber}`);
    return response.data;
};

export const compareVersions = async (contractId, v1, v2) => {
    const response = await api.get(`${API_URL}/api/contracts/${contractId}/versions/${v1}/compare/${v2}`);
    return response.data;
};

// --- Export ---

export const exportDiff = async (source, target, sourceName = 'source', targetName = 'target', diffLines = []) => {
    const response = await api.post(`${API_URL}/api/export/diff`, {
        source,
        target,
        source_name: sourceName,
        target_name: targetName,
        diff_lines: diffLines
    }, { responseType: 'blob' });
    return response.data;
};

export const exportPatch = async (source, target, sourceName = 'source', targetName = 'target') => {
    const response = await api.post(`${API_URL}/api/export/patch`, {
        source,
        target,
        source_name: sourceName,
        target_name: targetName
    }, { responseType: 'blob' });
    return response.data;
};

export const exportJSON = async (source, target, sourceName = 'source', targetName = 'target', diffLines = []) => {
    const response = await api.post(`${API_URL}/api/export/json`, {
        source,
        target,
        source_name: sourceName,
        target_name: targetName,
        diff_lines: diffLines
    }, { responseType: 'blob' });
    return response.data;
};

export const exportPDF = async (source, target, sourceName = 'source', targetName = 'target', diffLines = [], additionsCount = null, deletionsCount = null) => {
    const response = await api.post(`${API_URL}/api/export/pdf`, {
        source,
        target,
        source_name: sourceName,
        target_name: targetName,
        diff_lines: diffLines,
        additions_count: additionsCount,
        deletions_count: deletionsCount
    }, { responseType: 'blob' });
    return response.data;
};

// EDI-specific exports with semantic annotations
export const exportEdiAnnotated = async (source, target, sourceName = 'source', targetName = 'target', includeUnchanged = false) => {
    const response = await api.post(`${API_URL}/api/export/edi-annotated`, {
        source,
        target,
        source_name: sourceName,
        target_name: targetName,
        include_unchanged: includeUnchanged
    }, { responseType: 'blob' });
    return response.data;
};

export const exportEdiHtml = async (source, target, sourceName = 'source', targetName = 'target') => {
    const response = await api.post(`${API_URL}/api/export/edi-html`, {
        source,
        target,
        source_name: sourceName,
        target_name: targetName
    }, { responseType: 'blob' });
    return response.data;
};

// Helper to trigger browser download from blob
export const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const deleteVersion = async (contractId, versionNumber) => {
    const response = await api.delete(`${API_URL}/api/contracts/${contractId}/versions/${versionNumber}`);
    return response.data;
};

// --- Action Logs ---

export const getActionLogs = async (contractId = null, limit = 50) => {
    const params = new URLSearchParams();
    if (contractId) params.append('contract_id', contractId);
    if (limit) params.append('limit', limit);

    const queryString = params.toString();
    const url = `${API_URL}/api/actions${queryString ? '?' + queryString : ''}`;
    const response = await api.get(url);
    return response.data;
};

export const getContractActionLogs = async (contractId) => {
    const response = await api.get(`${API_URL}/api/actions/contract/${contractId}`);
    return response.data;
};

// --- Tags & Categories ---

export const getTagCategories = async () => {
    const response = await api.get(`${API_URL}/api/tags/categories`);
    return response.data;
};

export const createTagCategory = async (data) => {
    const response = await api.post(`${API_URL}/api/tags/categories`, data);
    return response.data;
};

export const updateTagCategory = async (categoryId, data) => {
    const response = await api.patch(`${API_URL}/api/tags/categories/${categoryId}`, data);
    return response.data;
};

export const deleteTagCategory = async (categoryId) => {
    const response = await api.delete(`${API_URL}/api/tags/categories/${categoryId}`);
    return response.data;
};

export const createTag = async (data) => {
    const response = await api.post(`${API_URL}/api/tags/`, data);
    return response.data;
};

export const deleteTag = async (tagId) => {
    const response = await api.delete(`${API_URL}/api/tags/${tagId}`);
    return response.data;
};

export const assignTagsToContract = async (contractId, tagIds) => {
    const response = await api.post(`${API_URL}/api/tags/assign`, {
        contract_id: contractId,
        tag_ids: tagIds
    });
    return response.data;
};

export const getContractTags = async (contractId) => {
    const response = await api.get(`${API_URL}/api/tags/contract/${contractId}`);
    return response.data;
};

// --- Vendors (shortcut functions) ---

export const getVendors = async () => {
    // Use dedicated vendors endpoint (more efficient than loading all contracts)
    const response = await api.get(`${API_URL}/api/contracts/vendors`);
    return response.data;
};

// --- Members ---

export const getMembers = async () => {
    const response = await api.get(`${API_URL}/api/members`);
    return response.data;
};

export const removeMember = async (userId) => {
    const response = await api.delete(`${API_URL}/api/members/${userId}`);
    return response.data;
};

export const updateMemberRole = async (userId, role) => {
    const response = await api.put(`${API_URL}/api/members/${userId}/role`, { role });
    return response.data;
};

// --- Slack Settings ---

export const getSlackSettings = async () => {
    const response = await api.get(`${API_URL}/api/slack-settings/`);
    return response.data;
};

export const updateSlackSettings = async (settings) => {
    const response = await api.put(`${API_URL}/api/slack-settings/`, settings);
    return response.data;
};

export const testSlackWebhook = async () => {
    const response = await api.post(`${API_URL}/api/slack-settings/test`);
    return response.data;
};

// ─── API Keys ───────────────────────────────────────────────────────────

export const getApiKeys = async () => {
    const response = await api.get(`${API_URL}/api/api-keys/`);
    return response.data;
};

export const createApiKey = async (name, scopes = []) => {
    const response = await api.post(`${API_URL}/api/api-keys/`, { name, scopes });
    return response.data;
};

export const revokeApiKey = async (keyId) => {
    const response = await api.delete(`${API_URL}/api/api-keys/${keyId}`);
    return response.data;
};

export const resendInvitation = async (invitationId) => {
    const response = await api.post(`${API_URL}/api/invitations/${invitationId}/resend`);
    return response.data;
};

export const getInvoices = async () => {
    const response = await api.get(`${API_URL}/api/stripe/invoices`);
    return response.data;
};

export const purgeAuditLogs = async (olderThanDays = 90) => {
    const response = await api.delete(`${API_URL}/api/actions/purge?older_than_days=${olderThanDays}`);
    return response.data;
};

// ─── Usage Statistics ────────────────────────────────────────────────

export const getMyUsage = async () => {
    const response = await api.get(`${API_URL}/api/users/me/usage`);
    return response.data;
};

export const getAuditLogs = async () => {
    const response = await api.get(`${API_URL}/api/actions`);
    return response.data;
};

export const updateOrganization = async (name, slug) => {
    const response = await api.put(`${API_URL}/api/users/me/organizations/current`, { name, slug });
    return response.data;
};

// ─── Org Backup ───────────────────────────────────────────────────────────

/**
 * Download a full JSON backup of the organization's data.
 * Triggers a browser file download automatically.
 */
export const downloadOrganizationBackup = async () => {
    const response = await api.get(`${API_URL}/api/users/me/organizations/backup`, {
        responseType: 'blob',
    });

    // Extract filename from Content-Disposition header (if present)
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'driftops_backup.json';

    // Create a temporary anchor and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Import organization data from a DriftOps backup JSON file.
 * @param {File} file - The .json backup file selected by the user
 * @returns {Promise<object>} - Import summary from the backend
 */
export const importOrganizationBackup = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${API_URL}/api/users/me/organizations/restore`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};
